'use client';

import Box from '@mui/material/Box';
import { useCallback, useState } from 'react';
import { animated, to, type SpringValue } from 'react-spring';
import {
    Flödesspår,
    GRUND,
    Gradientfält,
    ScenVäxlare,
    Siffervisare,
    TYPSNITT,
    djup,
    hexTillRgba,
    ljus,
    type ScenKontext
} from 'src/components/rörelse';
import { fNummerMedSpacing } from 'src/utils/format-number';
import { PresentationsNav } from '../komponenter/PresentationsNav';
import { ScenSektion } from '../komponenter/ScenSektion';
import { hittaPresentation } from '../presentationer';
import { KONTON, KÄLLOR, REPRESENTATIONER, TRANSAKTIONER } from './bank-innehåll';

const AnimeradDiv = animated('div');
const KOMBO = 'grön' as const;

/** Ljuskällan vandrar med läsaren i stället för att byta färg. Sidan har en färg. */
const GLÖDPOSITIONER: [number, number][] = [
    [22, 24],
    [76, 38],
    [30, 58],
    [70, 62],
    [38, 74],
    [64, 34]
];

export function BankView() {
    const presentation = hittaPresentation('bank');
    const [glödIndex, sättGlödIndex] = useState(0);

    const glöd = useCallback(
        (index: number) => (synlig: boolean) => {
            if (synlig) sättGlödIndex(index);
        },
        []
    );

    return (
        <>
            <Gradientfält
                kombinationer={GLÖDPOSITIONER.map(() => KOMBO)}
                positioner={GLÖDPOSITIONER}
                index={glödIndex}
                styrka={0.42}
            />

            <ScenSektion
                id="konton"
                kombo={KOMBO}
                nivå="h1"
                rubrik={presentation.titel}
                brödtext={[
                    presentation.ingress,
                    'Föreningen har sällan bara ett konto. Transaktionskonto, sparkonto, placeringar och fonderade medel ligger i samma vy, var och en knuten till sitt bokföringskonto.'
                ]}
                onSynlig={glöd(0)}
            >
                <Kontoliggare />
            </ScenSektion>

            <ScenSektion
                id="flode"
                kombo={KOMBO}
                spegla
                rubrik="Flödet kommer in av sig självt"
                brödtext={[
                    'Kopplade konton hämtas varje natt. Transaktioner som redan finns känns igen på belopp, konto och saldo — samma rad läses aldrig in två gånger, oavsett om den kom från bankkopplingen eller ur ett uppladdat kontoutdrag.'
                ]}
                onSynlig={glöd(1)}
            >
                {({ framsteg }: ScenKontext) => <Transaktionsström framsteg={framsteg} />}
            </ScenSektion>

            <ScenSektion
                id="koppling"
                kombo={KOMBO}
                rubrik="Och varje rad får sin affärshändelse"
                brödtext={[
                    'Det här är hela poängen. En banktransaktion är en rad pengar som rört sig; en affärshändelse är vad som faktiskt hände. Appen föreslår kopplingen utifrån belopp och datum, och du bekräftar den med en knapp i transaktionens detaljvy.',
                    'Det som inte gick att matcha automatiskt blir kvar i en lista — inte tyst borttappat.'
                ]}
                onSynlig={glöd(2)}
            >
                {({ framsteg }: ScenKontext) => <Kopplingsspår framsteg={framsteg} />}
            </ScenSektion>

            <ScenSektion
                id="avstamning"
                kombo={KOMBO}
                spegla
                rubrik="Tills ingenting står oförklarat"
                brödtext={[
                    'Avstämningen jämför bokfört saldo på 1930 mot bankens eget saldo och listar det som skiljer. Så här kan en period se ut när den arbetats igenom.'
                ]}
                onSynlig={glöd(3)}
            >
                <Avstämning />
            </ScenSektion>

            <ScenSektion
                id="representationer"
                kombo={KOMBO}
                rubrik="Samma händelse, tre sanningar"
                brödtext={[
                    'Banken, föreningen och bokföringen beskriver samma sak på tre sätt. Appen håller ihop dem, så att du kan gå från vilken som helst av dem till de andra två.'
                ]}
                onSynlig={glöd(4)}
            >
                <Representationer />
            </ScenSektion>

            <ScenSektion
                id="kallor"
                kombo={KOMBO}
                spegla
                höjd="auto"
                rubrik="Två vägar in"
                brödtext={KÄLLOR.map((k) => k.text)}
                onSynlig={glöd(5)}
            >
                <Flödesspår
                    noder={KÄLLOR.map((k) => ({ id: k.id, etikett: k.etikett, under: k.under }))}
                    kombo={KOMBO}
                    riktning="vågrät"
                    etikett="Källor för banktransaktioner"
                />
            </ScenSektion>

            <PresentationsNav nuvarande="bank" />
        </>
    );
}

function Kontoliggare() {
    const summa = KONTON.reduce((acc, k) => acc + k.saldo, 0);

    return (
        <Box aria-label="Föreningens konton" sx={{ width: '100%', maxWidth: 520 }}>
            {KONTON.map((konto, i) => (
                <Box
                    key={konto.id}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        alignItems: 'baseline',
                        gap: 2,
                        py: 2,
                        borderBottom: `1px solid ${GRUND.kant}`
                    }}
                >
                    <Box>
                        <Box sx={{ fontSize: 15.5, color: GRUND.text }}>{konto.namn}</Box>
                        <Box sx={{ fontSize: 12.5, color: GRUND.dämpad, mt: 0.3 }}>
                            Bokföringskonto {konto.bokförings}
                        </Box>
                    </Box>
                    <Siffervisare
                        till={konto.saldo}
                        suffix=" kr"
                        fördröjning={140 + i * 130}
                        variant="h4"
                        component="span"
                        sx={{
                            fontFamily: TYPSNITT.display,
                            fontVariationSettings: "'opsz' 40",
                            fontWeight: 600,
                            fontSize: 'clamp(1.15rem, 2.4vw, 1.6rem)',
                            letterSpacing: '-0.02em',
                            color: GRUND.text,
                            whiteSpace: 'nowrap'
                        }}
                    />
                </Box>
            ))}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    alignItems: 'baseline',
                    gap: 2,
                    pt: 2.25
                }}
            >
                <Box sx={{ fontSize: 13.5, color: GRUND.dämpad, letterSpacing: '0.01em' }}>
                    Summa likvida medel
                </Box>
                <Siffervisare
                    till={summa}
                    suffix=" kr"
                    fördröjning={700}
                    variant="h3"
                    component="span"
                    sx={{
                        fontFamily: TYPSNITT.display,
                        fontVariationSettings: "'opsz' 72",
                        fontWeight: 600,
                        fontSize: 'clamp(1.5rem, 3.4vw, 2.25rem)',
                        letterSpacing: '-0.03em',
                        color: ljus(KOMBO),
                        whiteSpace: 'nowrap'
                    }}
                />
            </Box>
        </Box>
    );
}

function Transaktionsström({ framsteg }: { framsteg: SpringValue<number> }) {
    return (
        <Box role="list" aria-label="Inkommande banktransaktioner" sx={{ width: '100%', maxWidth: 520 }}>
            {TRANSAKTIONER.map((rad, i) => {
                const tröskel = i / TRANSAKTIONER.length;
                return (
                    <AnimeradDiv
                        key={rad.id}
                        role="listitem"
                        style={{
                            opacity: framsteg.to((p) => (p >= tröskel ? 1 : 0.2)),
                            transform: framsteg.to((p) => `translateY(${p >= tröskel ? 0 : 26}px)`),
                            willChange: 'transform, opacity'
                        }}
                    >
                        <Transaktionsrad rad={rad} />
                    </AnimeradDiv>
                );
            })}
        </Box>
    );
}

function Kopplingsspår({ framsteg }: { framsteg: SpringValue<number> }) {
    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 640,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr auto 168px' },
                alignItems: 'center',
                columnGap: 0,
                rowGap: 1
            }}
        >
            {TRANSAKTIONER.slice(0, 4).map((rad, i) => {
                const tröskel = (i + 0.4) / 4;
                return (
                    <Box key={rad.id} sx={{ display: 'contents' }}>
                        <Transaktionsrad rad={rad} kompakt />

                        <AnimeradDiv
                            aria-hidden
                            style={{
                                height: 1,
                                width: 44,
                                background: ljus(KOMBO),
                                transformOrigin: 'left',
                                transform: framsteg.to((p) => `scaleX(${p >= tröskel ? 1 : 0})`),
                                display: 'block'
                            }}
                        />

                        <AnimeradDiv
                            style={{
                                opacity: framsteg.to((p) => (p >= tröskel ? 1 : 0.22)),
                                transform: to([framsteg], (p) => `scale(${p >= tröskel ? 1 : 0.9})`)
                            }}
                        >
                            <Box
                                sx={{
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: 1.5,
                                    textAlign: 'center',
                                    fontSize: 12.5,
                                    lineHeight: 1.3,
                                    color: GRUND.text,
                                    bgcolor: hexTillRgba(djup(KOMBO), 0.34),
                                    border: `1px solid ${hexTillRgba(ljus(KOMBO), 0.42)}`
                                }}
                            >
                                {rad.motpart}
                                <Box sx={{ fontSize: 11, color: GRUND.dämpad, mt: 0.25 }}>Affärshändelse</Box>
                            </Box>
                        </AnimeradDiv>
                    </Box>
                );
            })}
        </Box>
    );
}

function Transaktionsrad({
    rad,
    kompakt
}: {
    rad: (typeof TRANSAKTIONER)[number];
    kompakt?: boolean;
}) {
    const negativt = rad.belopp < 0;
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 2,
                py: kompakt ? 1.1 : 1.5,
                px: kompakt ? 1.5 : 2,
                borderRadius: 1.5,
                border: `1px solid ${GRUND.kant}`,
                bgcolor: hexTillRgba('#12151F', 0.7),
                mb: kompakt ? 0 : 1
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Box sx={{ fontSize: kompakt ? 13 : 14.5, color: GRUND.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {rad.referens}
                </Box>
                <Box sx={{ fontSize: 11.5, color: GRUND.dämpad, mt: 0.2 }}>{rad.datum}</Box>
            </Box>
            <Box
                data-tal
                sx={{
                    fontSize: kompakt ? 13 : 14.5,
                    whiteSpace: 'nowrap',
                    color: negativt ? GRUND.dämpad : ljus(KOMBO)
                }}
            >
                {negativt ? '−' : '+'}
                {fNummerMedSpacing(Math.abs(rad.belopp))} kr
            </Box>
        </Box>
    );
}

function Avstämning() {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%', maxWidth: 460 }}>
            <Box>
                <Box sx={{ fontSize: 14, color: GRUND.dämpad, mb: 1 }}>Okopplade transaktioner</Box>
                {/*
                  Utgångsläget står som text, inte bara som räknarens startvärde. Vid mindre
                  rörelse ser läsaren annars enbart nollan, och hela poängen — att det fanns
                  143 att reda ut — hade burits av rörelsen ensam.
                */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
                    <Box
                        data-tal
                        sx={{
                            fontFamily: TYPSNITT.display,
                            fontVariationSettings: "'opsz' 72",
                            fontSize: 'clamp(1.6rem, 3.6vw, 2.4rem)',
                            fontWeight: 500,
                            letterSpacing: '-0.03em',
                            color: GRUND.dämpad
                        }}
                    >
                        143
                    </Box>
                    <Box aria-hidden sx={{ color: GRUND.dämpad, fontSize: '1.4rem' }}>
                        &rarr;
                    </Box>
                    <Siffervisare
                        till={0}
                        från={143}
                        variant="h1"
                        component="span"
                        sx={{
                            fontFamily: TYPSNITT.display,
                            fontVariationSettings: "'opsz' 120",
                            fontWeight: 600,
                            fontSize: 'clamp(3.4rem, 8vw, 5.6rem)',
                            lineHeight: 1,
                            letterSpacing: '-0.04em',
                            color: ljus(KOMBO)
                        }}
                    />
                </Box>
            </Box>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    rowGap: 1.25,
                    columnGap: 3,
                    fontSize: 14.5,
                    borderTop: `1px solid ${GRUND.kant}`,
                    pt: 2.5
                }}
            >
                {[
                    ['Bokfört saldo 1930', '842 150,00 kr'],
                    ['Saldo enligt banken', '842 150,00 kr'],
                    ['Differens', '0,00 kr']
                ].map(([etikett, värde]) => (
                    <Box key={etikett} sx={{ display: 'contents' }}>
                        <Box sx={{ color: GRUND.dämpad }}>{etikett}</Box>
                        <Box data-tal sx={{ color: GRUND.text, textAlign: 'right' }}>
                            {värde}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function Representationer() {
    return (
        <Box sx={{ width: '100%', maxWidth: 480 }}>
            <ScenVäxlare
                etikett="Samma händelse i tre representationer"
                kombo={KOMBO}
                höjd={250}
                scener={REPRESENTATIONER.map((r) => ({
                    nyckel: r.nyckel,
                    etikett: r.etikett,
                    innehåll: (
                        <Box
                            sx={{
                                height: '100%',
                                p: 2.5,
                                borderRadius: 2,
                                border: `1px solid ${hexTillRgba(ljus(KOMBO), 0.32)}`,
                                bgcolor: hexTillRgba('#12151F', 0.8)
                            }}
                        >
                            <Box
                                sx={{
                                    fontFamily: TYPSNITT.display,
                                    fontVariationSettings: "'opsz' 36",
                                    fontSize: '1.15rem',
                                    fontWeight: 600,
                                    letterSpacing: '-0.015em',
                                    mb: 2,
                                    color: GRUND.text
                                }}
                            >
                                {r.rubrik}
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 1, columnGap: 2, fontSize: 13.5 }}>
                                {r.rader.map(([etikett, värde]) => (
                                    <Box key={etikett} sx={{ display: 'contents' }}>
                                        <Box sx={{ color: GRUND.dämpad }}>{etikett}</Box>
                                        <Box data-tal sx={{ color: GRUND.text, textAlign: 'right' }}>
                                            {värde}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )
                }))}
            />
        </Box>
    );
}
